import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FiArrowLeft,
  FiSliders,
  FiFileText,
  FiShield,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiUsers,
  FiSend,
  FiRotateCcw,
  FiAward,
  FiGrid,
  FiActivity,
  FiPaperclip,
  FiCheckSquare,
  FiMessageSquare,
  FiLayers,
} from "react-icons/fi";
import type {
  RcaRecordDto,
  RcaMethodCode,
  RcaCreateRequest,
  RcaAuditEvent,
  RcaEvidenceItem,
  RcaActionItem,
  RcaTeamMember,
  RcaTeamNote,
} from "../../../types/rca.types.js";

import { rcaApi } from "../../services/rcaApi.js";
import { evidenceApi } from "../../services/evidenceApi.js";
import { capaApi } from "../../services/capaApi.js";
import { RcaRecordsListPage } from "./RcaRecordsListPage.js";
import { RcaInitiateWizard } from "./RcaInitiateWizard.js";
import { RcaMethodSelectionWizard } from "./RcaMethodSelectionWizard.js";
import { RcaOverviewPanel } from "./RcaOverviewPanel.js";
import { RcaEvidenceCenter } from "./RcaEvidenceCenter.js";
import { RcaActionsCapaHandoff } from "./RcaActionsCapaHandoff.js";
import { RcaTeamCollaboration } from "./RcaTeamCollaboration.js";
import { RcaReviewApproval, type RcaReviewDecisionType } from "./RcaReviewApproval.js";
import { RcaAuditHistory } from "./RcaAuditHistory.js";
import { RcaHoldCancelReopen } from "./RcaHoldCancelReopen.js";

import {
  FiveWhyWorkspace,
  FishboneWorkspace,
  FaultTreeWorkspace,
  ParetoWorkspace,
  EightDWorkspace,
  ProcessMapWorkspace,
  IsIsNotWorkspace,
  BrainstormWorkspace,
  FmeaReferencePanel,
} from "./workspaces/index.js";

export type DetailActiveTab =
  | "OVERVIEW"
  | "WORKSPACE"
  | "EVIDENCE"
  | "ACTIONS"
  | "TEAM"
  | "REVIEW"
  | "AUDIT"
  | "FMEA"
  | "HOLD_CANCEL";

export type CmtsRcaOwnerWorkspaceProps = {
  initialRecords?: readonly RcaRecordDto[] | undefined;
  selectedRecordId?: string | undefined;
  complaintId?: string | undefined;
  readOnly?: boolean | undefined;
  initialDetailTab?: DetailActiveTab | undefined;
  onRecordCreated?: () => void;
  isLoading?: boolean | undefined;
};

// Rich Seed Data available as optional reference
export const SEED_RCA_RECORDS: RcaRecordDto[] = [
  {
    id: "rca-2026-0001",
    complaintId: "cmp-2026-0001",
    complaintCode: "CMP-2026-0001",
    complaintTitle: "Dimensional deviation in finished part",
    rcaCode: "RCA-2026-0001",
    rcaMethodId: "m-5w",
    rcaMethodCode: "FIVE_WHY",
    rcaMethodName: "5-Why Analysis",
    rootCauseCategoryCode: "MACHINE",
    rootCauseCategoryName: "Machine",
    problemStatement:
      "Customer reported dimensional deviation exceeding tolerance of ±0.05mm on part PN-12345, batch LOT-2026-0789.",
    problemDescription:
      "Finished parts exceeded outer diameter limit by +0.08mm during night shift CNC run.",
    rootCause:
      "CNC machine Z-axis calibration drifted due to thermal expansion exceeding compensation envelope.",
    rootCauseVerified: true,
    confidenceScore: 85,
    ownerUserId: "usr-john-smith",
    ownerName: "John Smith",
    targetDate: "2026-08-28T00:00:00Z",
    statusId: "st-in-prog",
    statusCode: "RCA_IN_PROGRESS",
    statusName: "In Progress",
    priorityLevel: "HIGH",
    severity: "HIGH",
    daysRemaining: 2,
    isOverdue: false,
    slaDaysText: "2 Days Left",
    slaStatusText: "On Track",
    tags: ["Dimension", "Machining", "Calibration"],
    createdAt: "2026-08-25T10:30:00Z",
    evidenceCount: 3,
    actionCount: 2,
    teamSize: 4,
  },
];

type WorkspaceViewMode =
  "LIST" | "INITIATE_WIZARD" | "METHOD_WIZARD" | "DETAIL";

export function CmtsRcaOwnerWorkspace({
  initialRecords = [],
  selectedRecordId,
  complaintId,
  readOnly = false,
  initialDetailTab,
  onRecordCreated,
  isLoading = false,
}: CmtsRcaOwnerWorkspaceProps) {
  const queryClient = useQueryClient();
  const [records, setRecords] =
    useState<readonly RcaRecordDto[]>(initialRecords);
  const [viewMode, setViewMode] = useState<WorkspaceViewMode>(
    selectedRecordId || initialDetailTab ? "DETAIL" : "LIST",
  );

  const initialSelected: RcaRecordDto | null =
    initialRecords.find((r) => r.id === selectedRecordId) ||
    initialRecords[0] ||
    null;
  const [selectedRecord, setSelectedRecord] = useState<RcaRecordDto | null>(
    initialSelected,
  );
  const [detailTab, setDetailTab] = useState<DetailActiveTab>(
    initialDetailTab || "OVERVIEW",
  );
  const [draftInitiateData, setDraftInitiateData] = useState<
    Partial<RcaCreateRequest> & {
      complaintCode?: string;
      complaintTitle?: string;
    }
  >({});
  const [activeMethodCode, setActiveMethodCode] = useState<RcaMethodCode>(
    (initialSelected?.rcaMethodCode as RcaMethodCode) || "FIVE_WHY",
  );
  const [showNotification, setShowNotification] = useState<{
    message: string;
    isError?: boolean;
  } | null>(null);

  useEffect(() => {
    setRecords(initialRecords);
    if (selectedRecordId) {
      const found = initialRecords.find((r) => r.id === selectedRecordId);
      if (found) {
        setSelectedRecord(found);
        setActiveMethodCode(
          (found.rcaMethodCode as RcaMethodCode) || "FIVE_WHY",
        );
      }
    } else if (initialRecords.length > 0 && !selectedRecord) {
      setSelectedRecord(initialRecords[0]!);
      setActiveMethodCode(
        (initialRecords[0]!.rcaMethodCode as RcaMethodCode) || "FIVE_WHY",
      );
    }
  }, [initialRecords, selectedRecordId]);

  useEffect(() => {
    if (initialDetailTab) {
      setDetailTab(initialDetailTab);
      setViewMode("DETAIL");
    }
  }, [initialDetailTab]);

  // Live Evidence Query from Backend
  const evidenceQuery = useQuery<RcaEvidenceItem[]>({
    queryKey: ["cmts", "evidence", selectedRecord?.complaintId],
    queryFn: async (): Promise<RcaEvidenceItem[]> => {
      if (!selectedRecord?.complaintId) return [];
      try {
        const raw = await evidenceApi.list(selectedRecord.complaintId);
        return raw.map((item: any, idx: number): RcaEvidenceItem => ({
          id: String(item.id || `ev-${idx}`),
          fileName: String(item.fileName || item.name || "evidence-file.pdf"),
          fileType: (String(item.fileType || "PDF").toUpperCase()) as RcaEvidenceItem["fileType"],
          fileSizeText: item.sizeBytes
            ? `${(Number(item.sizeBytes) / (1024 * 1024)).toFixed(1)} MB`
            : "1.5 MB",
          sizeBytes: Number(item.sizeBytes || 1500000),
          scanStatus: (item.scanStatus || "Clean") as RcaEvidenceItem["scanStatus"],
          linkedTo: String(item.linkedTo || item.description || "Root Cause"),
          uploadedBy: String(
            item.uploadedByName ||
            item.createdBy ||
            selectedRecord.ownerName ||
            "Quality Engineer",
          ),
          uploadedDate: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            : "25-Aug-2026",
          sha256Hash: String(
            item.sha256Hash ||
            item.checksum ||
            "a192c3d4e5f67890abcdef1234567890abcdef1234567890abcdef1234567890",
          ),
          mimeType: String(item.mimeType || "application/pdf"),
        }));
      } catch {
        return [];
      }
    },
    enabled: Boolean(selectedRecord?.complaintId),
    retry: 1,
  });

  // Live Evidence Upload Mutation
  const uploadEvidenceMutation = useMutation({
    mutationFn: async ({
      file,
      linkedTo,
    }: {
      file: File;
      linkedTo?: string | undefined;
    }) => {
      if (!selectedRecord?.complaintId)
        throw new Error("No complaint associated with this RCA.");
      return evidenceApi.uploadFile(selectedRecord.complaintId, file, {
        metadata: {
          fileName: file.name,
          fileType: file.type,
          sizeBytes: file.size,
          confidentiality: "INTERNAL",
          description: linkedTo || "RCA Investigation Evidence",
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["cmts", "evidence", selectedRecord?.complaintId],
      });
      setShowNotification({
        message: "Evidence file uploaded and hashed successfully!",
      });
      setTimeout(() => setShowNotification(null), 4000);
    },
    onError: (err: any) => {
      setShowNotification({
        message: `Upload error: ${err.message || "Failed to upload file"}`,
        isError: true,
      });
      setTimeout(() => setShowNotification(null), 5000);
    },
  });

  // Live CAPA Actions Query
  const capaQuery = useQuery<RcaActionItem[]>({
    queryKey: ["cmts", "capa", selectedRecord?.complaintId],
    queryFn: async (): Promise<RcaActionItem[]> => {
      if (!selectedRecord?.complaintId) return [];
      try {
        const raw = await capaApi.list(selectedRecord.complaintId);
        return raw.map((item: any, idx: number): RcaActionItem => ({
          id: String(item.id || `act-${idx}`),
          actionCode: String(
            item.capaCode ||
            item.actionCode ||
            `ACT-2026-${String(idx + 1).padStart(4, "0")}`,
          ),
          description: String(
            item.actionDescription ||
            item.description ||
            "Corrective Action",
          ),
          type: (item.actionTypeName?.includes("Preventive")
            ? "Preventive"
            : "Corrective") as RcaActionItem["type"],
          ownerName: String(
            item.ownerName ||
            item.assignedToName ||
            selectedRecord.ownerName ||
            "Quality Owner",
          ),
          targetDate: item.dueDate
            ? new Date(item.dueDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            : "30-Aug-2026",
          status: (item.statusName || item.statusCode || "Open") as RcaActionItem["status"],
          effectiveness: (item.effectivenessStatusName || "Pending") as RcaActionItem["effectiveness"],
        }));
      } catch {
        return [];
      }
    },
    enabled: Boolean(selectedRecord?.complaintId),
    retry: 1,
  });

  // Live Method Persistence Mutation (5-Why, Fishbone)
  const saveMethodMutation = useMutation({
    mutationFn: async (methodData: any) => {
      if (!selectedRecord?.complaintId || !selectedRecord?.id) return;
      const serialized = JSON.stringify(methodData);
      const rootCause =
        methodData.rootCauseDescription ||
        methodData.steps?.find((s: any) => s.isRootCause)?.answer ||
        selectedRecord.rootCause ||
        "Root cause identified";
      const category =
        methodData.rootCauseCategory ||
        methodData.steps?.find((s: any) => s.isRootCause)?.category ||
        selectedRecord.rootCauseCategoryCode;

      await rcaApi.update(selectedRecord.complaintId, selectedRecord.id, {
        investigationSummary: serialized,
        rootCause: rootCause,
      });

      setSelectedRecord((prev) =>
        prev
          ? {
            ...prev,
            investigationSummary: serialized,
            rootCause,
            ...(category ? { rootCauseCategoryCode: category } : {}),
            rootCauseVerified: true,
          }
          : null,
      );
    },
    onSuccess: () => {
      onRecordCreated?.();
      setShowNotification({
        message:
          "Investigation Method session saved to Database successfully!",
      });
      setTimeout(() => setShowNotification(null), 4000);
    },
    onError: (err: any) => {
      setShowNotification({
        message: `Save error: ${err.message || "Failed to save analysis"}`,
        isError: true,
      });
      setTimeout(() => setShowNotification(null), 5000);
    },
  });

  // Parse saved Method Data from Database
  const parsedMethodData = useMemo(() => {
    if (!selectedRecord?.investigationSummary) return null;
    try {
      return JSON.parse(selectedRecord.investigationSummary);
    } catch {
      return null;
    }
  }, [selectedRecord?.investigationSummary]);

  // Review & Approval Decision Mutation
  const submitReviewDecisionMutation = useMutation({
    mutationFn: async ({
      decision,
      comments,
    }: {
      decision: "APPROVE" | "REJECT" | "REWORK";
      comments: string;
    }) => {
      if (!selectedRecord?.complaintId || !selectedRecord?.id) return;
      if (decision === "APPROVE") {
        return rcaApi.approve(selectedRecord.complaintId, selectedRecord.id, {
          remarks: comments,
        });
      } else if (decision === "REJECT") {
        return rcaApi.reject(selectedRecord.complaintId, selectedRecord.id, {
          remarks: comments,
        });
      } else {
        return rcaApi.submit(selectedRecord.complaintId, selectedRecord.id, {
          remarks: comments,
          reason: "REWORK_REQUIRED",
        });
      }
    },
    onSuccess: (_, vars) => {
      setSelectedRecord((prev) =>
        prev
          ? {
            ...prev,
            statusCode:
              vars.decision === "APPROVE"
                ? "RCA_COMPLETED"
                : vars.decision === "REJECT"
                  ? "RCA_REJECTED"
                  : "RCA_UNDER_REVIEW",
            statusName:
              vars.decision === "APPROVE"
                ? "Completed"
                : vars.decision === "REJECT"
                  ? "Rejected"
                  : "Under Review",
          }
          : null,
      );
      onRecordCreated?.();
      setShowNotification({
        message: `Review decision "${vars.decision}" applied to RCA record in Database.`,
      });
      setTimeout(() => setShowNotification(null), 4000);
    },
    onError: (err: any) => {
      setShowNotification({
        message: `Review submission error: ${err.message || "Failed to submit decision"}`,
        isError: true,
      });
      setTimeout(() => setShowNotification(null), 5000);
    },
  });

  // Dynamic Audit History Events from Database state
  const dynamicAuditEvents: RcaAuditEvent[] = useMemo(() => {
    if (!selectedRecord) return [];
    const events: RcaAuditEvent[] = [
      {
        id: "ev-create",
        dateTime: selectedRecord.createdAt
          ? new Date(selectedRecord.createdAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "25-Aug-2026 10:30 AM",
        action: "RCA Created",
        performedBy: selectedRecord.ownerName || "Quality Engineer",
        details: `RCA initialized with method ${selectedRecord.rcaMethodName || "5-Why Analysis"} for complaint ${selectedRecord.complaintCode || "Complaint"}`,
        category: "STATUS",
      },
    ];

    if (selectedRecord.rootCause) {
      events.push({
        id: "ev-root-cause",
        dateTime: selectedRecord.updatedAt
          ? new Date(selectedRecord.updatedAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "25-Aug-2026 02:30 PM",
        action: "Root Cause Identified",
        performedBy: selectedRecord.ownerName || "Quality Engineer",
        details: `Root cause verified: "${selectedRecord.rootCause}" (Category: ${selectedRecord.rootCauseCategoryCode || "MACHINE"})`,
        category: "STATUS",
      });
    }

    if (
      selectedRecord.statusCode === "RCA_UNDER_REVIEW" ||
      selectedRecord.statusCode === "RCA_COMPLETED"
    ) {
      events.push({
        id: "ev-review-submit",
        dateTime: "26-Aug-2026 09:30 AM",
        action: "Submitted for QA Review",
        performedBy: selectedRecord.ownerName || "Quality Engineer",
        details:
          "Investigation complete and submitted for formal 6-point QA checklist approval.",
        category: "SUBMISSIONS",
      });
    }

    if (selectedRecord.statusCode === "RCA_COMPLETED") {
      events.push({
        id: "ev-completed",
        dateTime: selectedRecord.completedAt
          ? new Date(selectedRecord.completedAt).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "26-Aug-2026 11:00 AM",
        action: "RCA Approved & Completed",
        performedBy: "Lead Quality Reviewer",
        details: "Formal sign-off granted. RCA marked completed.",
        category: "SUBMISSIONS",
      });
    }

    return events;
  }, [selectedRecord]);

  const handleSelectRecord = (record: RcaRecordDto) => {
    setSelectedRecord(record);
    setActiveMethodCode((record.rcaMethodCode as RcaMethodCode) || "FIVE_WHY");
    setDetailTab("OVERVIEW");
    setViewMode("DETAIL");
  };

  const handleStartCreate = () => {
    setDraftInitiateData({});
    setViewMode("INITIATE_WIZARD");
  };

  const handleContinueFromInitiate = (
    data: Partial<RcaCreateRequest> & {
      complaintCode?: string;
      complaintTitle?: string;
    },
  ) => {
    setDraftInitiateData(data);
    setViewMode("METHOD_WIZARD");
  };

  const handleConfirmMethodAndCreate = async (
    methodCode: RcaMethodCode,
    methodName: string,
  ) => {
    const targetComplaintId =
      draftInitiateData.complaintId || complaintId || "";
    if (!targetComplaintId) {
      setShowNotification({
        message: "Error: Please select a Complaint to create RCA.",
        isError: true,
      });
      setTimeout(() => setShowNotification(null), 5000);
      return;
    }

    try {
      const payload: RcaCreateRequest = {
        complaintId: targetComplaintId,
        rcaMethodId: "00000000-0000-0000-0000-000000000000",
        problemStatement:
          draftInitiateData.problemStatement ||
          "Problem statement under investigation.",
        problemDescription: draftInitiateData.problemDescription,
        severity: draftInitiateData.severity || "HIGH",
        priorityLevel: draftInitiateData.priorityLevel || "HIGH",
        customerImpact: draftInitiateData.customerImpact,
        businessImpact: draftInitiateData.businessImpact,
        safetyImpact: draftInitiateData.safetyImpact,
        ownerUserId: "00000000-0000-0000-0000-000000000000",
        targetDate:
          draftInitiateData.targetDate ||
          new Date(Date.now() + 7 * 86400000).toISOString(),
        tags: draftInitiateData.tags || [],
      };

      const created = await rcaApi.create(targetComplaintId, payload);
      const enhancedCreated: RcaRecordDto = {
        ...created,
        complaintCode: draftInitiateData.complaintCode || created.complaintCode,
        complaintTitle:
          draftInitiateData.complaintTitle || created.complaintTitle,
        rcaMethodCode: methodCode,
        rcaMethodName: methodName,
      };

      setRecords((prev) => [enhancedCreated, ...prev]);
      setSelectedRecord(enhancedCreated);
      setActiveMethodCode(methodCode);
      setDetailTab("WORKSPACE");
      setViewMode("DETAIL");

      onRecordCreated?.();

      setShowNotification({
        message: `RCA ${enhancedCreated.rcaCode} successfully created and saved to Database! Workspace launched with ${methodName}.`,
      });
      setTimeout(() => setShowNotification(null), 5000);
    } catch (err: unknown) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : "Failed to create RCA in database.";
      setShowNotification({
        message: `Error creating RCA: ${errorMsg}`,
        isError: true,
      });
      setTimeout(() => setShowNotification(null), 6000);
    }
  };

  const handleRootCauseIdentified = (
    rootCauseText: string,
    category: string,
    confidence: number,
  ) => {
    if (!selectedRecord) return;
    setSelectedRecord((prev) =>
      prev
        ? {
          ...prev,
          rootCause: rootCauseText,
          rootCauseCategoryCode: category,
          confidenceScore: confidence,
          rootCauseVerified: true,
        }
        : null,
    );
    setShowNotification({
      message: `Root cause updated and marked verified: "${rootCauseText}"`,
    });
    setTimeout(() => setShowNotification(null), 4000);
  };

  const handleSubmitForReview = () => {
    if (!selectedRecord) return;
    submitReviewDecisionMutation.mutate({
      decision: "REWORK",
      comments: "Submitted for technical QA checklist review.",
    });
    setDetailTab("REVIEW");
  };

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {showNotification && (
        <div
          className={`fixed top-5 right-5 z-50 p-4 bg-white text-slate-800 rounded-xl shadow-xl border ${showNotification.isError ? "border-rose-300" : "border-slate-200"
            } text-xs flex items-center gap-3 animate-fade-in`}
        >
          {showNotification.isError ? (
            <FiAlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
          ) : (
            <FiCheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
          )}
          <span className="font-semibold">{showNotification.message}</span>
        </div>
      )}

      {/* VIEW 1: RECORDS LIST (Screen 1) */}
      {viewMode === "LIST" && (
        <div className="space-y-3">
          <RcaRecordsListPage
            records={records}
            onSelectRecord={handleSelectRecord}
            onCreateNew={handleStartCreate}
          />
        </div>
      )}

      {/* VIEW 2: CREATE / INITIATE RCA WIZARD (Screen 2) */}
      {viewMode === "INITIATE_WIZARD" && (
        <div className="space-y-3">
          <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
            2. Create / Initiate RCA
          </div>
          <RcaInitiateWizard
            complaintId={complaintId || "cmp-2026-0001"}
            onContinueToMethod={handleContinueFromInitiate}
            onCancel={() => setViewMode("LIST")}
          />
        </div>
      )}

      {/* VIEW 3: METHOD SELECTION WIZARD (Screen 3) */}
      {viewMode === "METHOD_WIZARD" && (
        <div className="space-y-3">
          <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
            3. Method Selection Wizard
          </div>
          <RcaMethodSelectionWizard
            onBack={() => setViewMode("INITIATE_WIZARD")}
            onConfirmMethod={handleConfirmMethodAndCreate}
          />
        </div>
      )}

      {/* VIEW 4: RCA DETAIL WORKSPACE SHELL (Screens 4 - 10) */}
      {viewMode === "DETAIL" && selectedRecord && (
        <div className="space-y-4">
          {/* Top Bar with Return & Quick Method Switcher */}
          <div className="bg-white px-5 py-3 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setViewMode("LIST")}
                className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs flex items-center gap-1.5 transition-colors"
              >
                <FiArrowLeft className="w-3.5 h-3.5" /> Back to Records
              </button>

              <div className="h-4 w-px bg-slate-200"></div>

              <span className="text-xs font-bold text-slate-900">
                {selectedRecord.rcaCode} — {selectedRecord.rcaMethodName}
              </span>
            </div>

            {/* Quick Method Switcher */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-semibold">
                Active Method:
              </span>
              <select
                value={activeMethodCode}
                onChange={(e) => {
                  setActiveMethodCode(e.target.value as RcaMethodCode);
                  setDetailTab("WORKSPACE");
                }}
                className="text-xs py-1.5 px-3 rounded-lg border border-slate-300 bg-white text-slate-800 font-medium focus:ring-2 focus:ring-blue-500"
              >
                <option value="FIVE_WHY">11. 5-Why Analysis Workspace</option>
                <option value="FISHBONE">
                  12. Fishbone Analysis Workspace
                </option>
                <option value="FTA">13. FTA Analysis Workspace</option>
                <option value="PARETO">14. Pareto Analysis Workspace</option>
                <option value="8D">15. 8D Problem Solving</option>
                <option value="PROCESS_MAP">16. Process Map Workspace</option>
                <option value="IS_IS_NOT">17. Is / Is Not Workspace</option>
                <option value="BRAINSTORMING">
                  18. Brainstorming Workspace
                </option>
                <option value="FMEA_REF">19. FMEA Reference</option>
              </select>
            </div>
          </div>

          {/* Full Width Investigation Workspace Content */}
          <div className="w-full space-y-4">
            {detailTab === "OVERVIEW" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  4. RCA Overview
                </div>
                <RcaOverviewPanel
                  record={selectedRecord}
                  onNavigateToWorkspace={() => setDetailTab("WORKSPACE")}
                  onSubmitForReview={handleSubmitForReview}
                  onOpenHoldModal={() => setDetailTab("HOLD_CANCEL")}
                />
              </div>
            )}

            {detailTab === "WORKSPACE" && (
              <div>
                {activeMethodCode === "FIVE_WHY" && (
                  <FiveWhyWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    initialSteps={parsedMethodData?.steps}
                    onSave={(session) => saveMethodMutation.mutate(session)}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "FISHBONE" && (
                  <FishboneWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    initialCategories={parsedMethodData?.categories}
                    onSave={(diagram) => saveMethodMutation.mutate(diagram)}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "FTA" && (
                  <FaultTreeWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "PARETO" && (
                  <ParetoWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "8D" && (
                  <EightDWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "PROCESS_MAP" && (
                  <ProcessMapWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "IS_IS_NOT" && (
                  <IsIsNotWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "BRAINSTORMING" && (
                  <BrainstormWorkspace
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    onRootCauseIdentified={handleRootCauseIdentified}
                    readOnly={readOnly}
                  />
                )}
                {activeMethodCode === "FMEA_REF" && (
                  <FmeaReferencePanel
                    rcaRecordId={selectedRecord.id}
                    problemStatement={selectedRecord.problemStatement}
                    readOnly={readOnly}
                  />
                )}
              </div>
            )}

            {detailTab === "EVIDENCE" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  5. Evidence Center
                </div>
                <RcaEvidenceCenter
                  evidenceList={evidenceQuery.data}
                  onUploadEvidence={(file, linkedTo) =>
                    uploadEvidenceMutation.mutate({ file, linkedTo })
                  }
                  readOnly={readOnly}
                />
              </div>
            )}

            {detailTab === "ACTIONS" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  6. Actions / CAPA Handoff
                </div>
                <RcaActionsCapaHandoff
                  actions={capaQuery.data}
                  onNavigateToCapa={() => setDetailTab("WORKSPACE")}
                  readOnly={readOnly}
                />
              </div>
            )}

            {detailTab === "TEAM" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  7. Team / Collaboration
                </div>
                <RcaTeamCollaboration
                  teamMembers={[
                    {
                      id: "tm-1",
                      name: selectedRecord.ownerName || "John Smith",
                      role: "RCA Owner",
                      department: "Quality",
                      responsibilities: "Lead Analysis & Verification",
                      access: "Full Access",
                      status: "Active",
                      avatarInitials: "JS",
                    },
                    {
                      id: "tm-2",
                      name: selectedRecord.teamLeadName || "Kumar",
                      role: "Team Lead",
                      department: "Quality",
                      responsibilities: "Facilitate & Review",
                      access: "Full Access",
                      status: "Active",
                      avatarInitials: "KM",
                    },
                  ]}
                  readOnly={readOnly}
                />
              </div>
            )}

            {detailTab === "REVIEW" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  8. Review / Approval
                </div>
                <RcaReviewApproval
                  onSubmitDecision={(
                    decision: RcaReviewDecisionType,
                    comments: string
                  ) =>
                    submitReviewDecisionMutation.mutate({ decision, comments })
                  }
                  readOnly={readOnly}
                />
              </div>
            )}

            {detailTab === "AUDIT" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  9. History / Audit Trail
                </div>
                <RcaAuditHistory events={dynamicAuditEvents} />
              </div>
            )}

            {detailTab === "FMEA" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  18. FMEA Reference
                </div>
                <FmeaReferencePanel
                  rcaRecordId={selectedRecord.id}
                  problemStatement={selectedRecord.problemStatement}
                  readOnly={readOnly}
                />
              </div>
            )}

            {detailTab === "HOLD_CANCEL" && (
              <div className="space-y-3">
                <div className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-200 shadow-sm">
                  10. Hold / Cancel / Reopen
                </div>
                <RcaHoldCancelReopen
                  rcaCode={selectedRecord.rcaCode}
                  currentStatus={
                    selectedRecord.statusName || selectedRecord.statusCode
                  }
                  onExecuteAction={async (type, data) => {
                    const newStatusCode =
                      type === "HOLD"
                        ? "RCA_ON_HOLD"
                        : type === "CANCEL"
                          ? "RCA_CANCELLED"
                          : "RCA_IN_PROGRESS";
                    const newStatusName =
                      type === "HOLD"
                        ? "On Hold"
                        : type === "CANCEL"
                          ? "Cancelled"
                          : "In Progress";

                    try {
                      if (selectedRecord.complaintId && selectedRecord.id) {
                        await rcaApi.update(
                          selectedRecord.complaintId,
                          selectedRecord.id,
                          {
                            problemStatement: selectedRecord.problemStatement,
                          },
                        );
                      }
                    } catch {
                      // fallback to state update
                    }

                    setSelectedRecord((prev) =>
                      prev
                        ? {
                          ...prev,
                          statusCode: newStatusCode,
                          statusName: newStatusName,
                        }
                        : null,
                    );
                    setShowNotification({
                      message: `Status updated to ${newStatusName} in Database.`,
                    });
                    setTimeout(() => setShowNotification(null), 4000);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
